import React, { useState, useEffect } from "react";
import { Layout, Typography, Button, Input, Modal, message, Spin } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import TopBar from "../features/TopBar/TopBar";
import { useUser } from "../context/UserContext";
import { authPpiApiAxiosWithRefresh } from "../services/api";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const VinHomePage: React.FC = () => {
    const [vin, setVin] = useState("");
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [vehicleDetails, setVehicleDetails] = useState<any>(null);
    const [docId, setDocId] = useState<string | null>(null);

    const { user, isLoading } = useUser();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Auto-analyze if redirected back from signup with vin and autoAnalyze=true
    useEffect(() => {
        const autoAnalyze = searchParams.get("autoAnalyze");
        const paramVin = searchParams.get("vin");
        if (user && autoAnalyze === "true" && paramVin && paramVin.length === 17) {
            setVin(paramVin.toUpperCase());
            handleAnalyze(paramVin.toUpperCase());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, searchParams]);

    const handleAnalyze = async (vinToAnalyze: string = vin) => {
        if (vinToAnalyze.length !== 17) {
            message.error("Please enter a valid 17-character VIN.");
            return;
        }

        if (!user) {
            // Redirect to signup and pass along the intent
            const returnUrl = `/vin?autoAnalyze=true&vin=${encodeURIComponent(vinToAnalyze)}`;
            navigate(`/signup?redirectTo=${encodeURIComponent(returnUrl)}`);
            return;
        }

        setLoading(true);
        try {
            const response = await (
                await authPpiApiAxiosWithRefresh()
            ).post(
                "/create-manual-ppi",
                { vin: vinToAnalyze },
                { headers: { "Content-Type": "application/json" } }
            );

            if (response.data.success) {
                const newDocId = response.data.doc_id;
                setDocId(newDocId);

                // Fetch the details to show the modal
                const dataResponse = await (
                    await authPpiApiAxiosWithRefresh()
                ).get(`/vehicle-data/${newDocId}`);

                if (dataResponse.data.success) {
                    setVehicleDetails(dataResponse.data.data.vehicleDetails || {});
                    setModalVisible(true);
                } else {
                    message.error("Failed to fetch vehicle details.");
                }
            } else {
                message.error(response.data.error || "Failed to create report.");
            }
        } catch (err: any) {
            message.error(err.response?.data?.error || err.message || "An error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const handleStartOver = () => {
        setModalVisible(false);
        setVin("");
        setDocId(null);
        setVehicleDetails(null);
        navigate("/vin", { replace: true });
    };

    const handleUpdateOrAdd = () => {
        setModalVisible(false);
        navigate(`/create-report?docId=${docId}&step=1`);
    };

    const handleContinue = () => {
        setModalVisible(false);
        // Bypass missing details step (2) and go to images (3)
        navigate(`/create-report?docId=${docId}&step=3`);
    };

    return (
        <Layout className="landing-page" style={{ minHeight: "100vh", backgroundColor: "#fff" }}>
            <TopBar className="title" title="" />

            <Content className="landing-content" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
                <div style={{ maxWidth: "600px", width: "100%", textAlign: "center" }}>
                    <div style={{ display: "inline-block", background: "#f0f5ff", color: "#1890ff", padding: "4px 12px", borderRadius: "16px", fontWeight: "bold", fontSize: "14px", marginBottom: "16px" }}>
                        VIN ANALYSIS
                    </div>
                    <Title level={1} style={{ marginBottom: "16px" }}>
                        A cheat sheet for any used-car listing.
                    </Title>
                    <Paragraph style={{ fontSize: "18px", color: "#666", marginBottom: "40px" }}>
                        Enter a VIN to get what to pay, what's likely wrong, and repair-cost ranges. Total transparency a seller won't give you.
                    </Paragraph>

                    <div className="vin-input-row">
                        <Input
                            placeholder="Enter VIN (17 characters)"
                            value={vin}
                            onChange={(e) => setVin(e.target.value.toUpperCase())}
                            maxLength={17}
                            onPressEnter={() => handleAnalyze()}
                            disabled={loading || isLoading}
                            className="vin-input"
                        />
                        <Button
                            type="primary"
                            loading={loading}
                            disabled={loading || isLoading || vin.length !== 17}
                            onClick={() => handleAnalyze()}
                            className="vin-analyze-btn"
                        >
                            Analyze
                        </Button>
                    </div>

                    <div style={{ marginTop: "20px", fontSize: "14px", color: "#888" }}>
                        First one free, then $5 per report • See what your car is worth
                    </div>
                </div>

                <Modal
                    title={<div style={{ textAlign: "center", fontSize: "20px", fontWeight: "bold" }}>Here is what we found...</div>}
                    visible={modalVisible}
                    onCancel={handleStartOver}
                    footer={null}
                    closable={false}
                    maskClosable={false}
                    centered
                    width={500}
                >
                    {vehicleDetails ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "24px" }}>
                            <div
                                style={{ background: "#f9f9f9", padding: "16px", borderRadius: "8px", border: "1px solid #eee", cursor: "pointer" }}
                                onClick={handleUpdateOrAdd}
                            >
                                <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: "8px" }}>
                                    <Text strong>VIN:</Text>
                                    <Text>{vehicleDetails.VIN || "N/A"}</Text>

                                    <Text strong>Year:</Text>
                                    <Text>{vehicleDetails.Year || "N/A"}</Text>

                                    <Text strong>Make:</Text>
                                    <Text>{vehicleDetails.Make || "N/A"}</Text>

                                    <Text strong>Model:</Text>
                                    <Text>{vehicleDetails.Model || "N/A"}</Text>

                                    <Text strong>Engine:</Text>
                                    <Text>{vehicleDetails.Engine || "N/A"}</Text>
                                </div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", textAlign: "center", gap: "8px" }}>
                                <Text onClick={handleUpdateOrAdd} style={{ color: "#1890ff", cursor: "pointer" }}>Update or add Info</Text>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                <Button type="primary" size="large" onClick={handleContinue} block style={{ height: "48px", fontSize: "16px", fontWeight: "bold" }}>
                                    Continue
                                </Button>

                                <div style={{ display: "flex", gap: "12px" }}>
                                    <Button size="large" onClick={handleStartOver} style={{ flex: 1 }} danger>
                                        Start over
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ textAlign: "center", padding: "40px" }}>
                            <Spin />
                        </div>
                    )}
                </Modal>

            </Content>
        </Layout>
    );
};

export default VinHomePage;
